import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { Gift, Music, Dumbbell, Wrench, Smartphone, Trophy, Users } from 'lucide-react';
interface Bonus {
  number: string;
  title: string;
  emoji: string;
  desc: string;
  bookColor: string; // tailwind bg color for the book cover
  spineColor: string; // slightly darker spine
  Icon: ComponentType<{
    className?: string;
  }>;
}
const bonuses: Bonus[] = [{
  number: '1',
  title: 'PISTA LIBRE',
  emoji: '🏟️',
  desc: 'Gestión de grupos mixtos y disciplina. El sistema de estaciones para manejar 3 niveles distintos en una sola clase.',
  bookColor: 'bg-[#DB2777]',
  spineColor: 'bg-[#BE185D]',
  Icon: Users
}, {
  number: '2',
  title: 'CUERPO REFORZADO',
  emoji: '💪',
  desc: 'Preparación física Off-Ice. Ejercicios de fuerza, flexibilidad y coordinación para potenciar el rendimiento en la pista sin necesidad de equipamiento.',
  bookColor: 'bg-[#059669]',
  spineColor: 'bg-[#047857]',
  Icon: Dumbbell
}, {
  number: '3',
  title: 'MAESTROS DEL RITMO',
  emoji: '🎵',
  desc: 'Guía de música y coreografía. Cómo elegir la música correcta, contar los tiempos y armar una coreografía que emocione al jurado.',
  bookColor: 'bg-[#9333EA]',
  spineColor: 'bg-[#7E22CE]',
  Icon: Music
}, {
  number: '4',
  title: 'MECÁNICA TOTAL',
  emoji: '🔧',
  desc: 'Guía completa de mantenimiento de patines. Rulemanes, ruedas, frenos y planchas. Lo que todo profe tiene que saber para asesorar a sus alumnos.',
  bookColor: 'bg-[#0D9488]',
  spineColor: 'bg-[#0F766E]',
  Icon: Wrench
}, {
  number: '5',
  title: 'CLUB DIGITAL',
  emoji: '📱',
  desc: 'Marketing para profesores de patín. Cómo llenar tus clases usando Instagram y llegar a padres y adultos de tu zona.',
  bookColor: 'bg-[#D97706]',
  spineColor: 'bg-[#B45309]',
  Icon: Smartphone
}, {
  number: '6',
  title: 'PUNTUACIÓN DE ORO',
  emoji: '🏆',
  desc: 'El nuevo sistema Rollart simplificado. Qué miran los jueces, cómo sumar QOE y cómo preparar alumnos de competencia sin adivinar.',
  bookColor: 'bg-[#9D174D]',
  spineColor: 'bg-[#831843]',
  Icon: Trophy
}];
const BookCover = ({
  bookColor,
  spineColor,
  number,
  Icon
}: {
  bookColor: string;
  spineColor: string;
  number: string;
  Icon: ComponentType<{
    className?: string;
  }>;
}) => <div className="relative w-[180px] h-[240px] mx-auto" data-id="element-14">
    {/* Soft shadow under book */}
    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-[80%] h-3 bg-black/15 blur-xl rounded-full" data-id="element-15" />

    {/* Spine (left edge) */}
    <div className={`absolute left-0 top-0 bottom-0 w-3 ${spineColor} rounded-l-lg`} data-id="element-16" />

    {/* Book front */}
    <div className={`absolute left-3 top-0 right-0 bottom-0 ${bookColor} rounded-r-lg shadow-xl flex flex-col items-center justify-between py-6 px-4`} data-id="element-17">
      {/* Icon at top */}
      <Icon className="w-8 h-8 text-white" data-id="element-18" />

      {/* Center text */}
      <div className="text-white text-center" data-id="element-19">
        <div className="text-xl font-extrabold tracking-tight" data-id="element-20">
          Bonus {number}
        </div>
      </div>

      {/* Footer with brand */}
      <div className="w-full" data-id="element-21">
        <div className="w-full h-px bg-white/30 mb-3" data-id="element-22" />
        <div className="text-white/80 text-[10px] font-bold tracking-widest text-center" data-id="element-23">
          PATÍN 360™
        </div>
      </div>
    </div>
  </div>;
export const Bonuses = () => {
  return <section className="py-16 md:py-24 bg-[#FDF4F8]" data-id="element-24">
      <div className="max-w-6xl mx-auto px-5" data-id="element-25">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16" data-id="element-26">
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold text-brand-950 mb-5 tracking-tight leading-[1.05]" data-id="element-27">
            + 6 Regalos Estratégicos
          </h2>
          <p className="text-base md:text-xl text-gray-600 mb-8 max-w-xl mx-auto leading-relaxed" data-id="element-28">
            Incluidos sin costo extra · Valor real del pack
          </p>

          {/* Rose "GRATIS POR EL DÍA DE HOY" pill */}
          <div className="inline-flex items-center gap-2 bg-[#DB2777] text-white font-bold px-6 py-3 rounded-full text-sm md:text-base shadow-md" data-id="element-29">
            <Gift className="w-4 h-4 md:w-5 md:h-5" data-id="element-30" />
            🎁 GRATIS POR EL DÍA DE HOY
          </div>
        </div>

        {/* Bonus cards grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-31">
          {bonuses.map((bonus, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-gray-100 flex flex-col items-center text-center" data-id="element-32">
              {/* Book mockup */}
              <div className="mb-6 md:mb-8" data-id="element-33">
                <BookCover bookColor={bonus.bookColor} spineColor={bonus.spineColor} number={bonus.number} Icon={bonus.Icon} data-id="element-34" />
              </div>

              {/* GRATIS pill */}
              <div className="inline-flex items-center bg-green-100 text-green-800 font-bold text-xs md:text-sm px-4 py-1.5 rounded-full mb-4" data-id="element-35">
                GRATIS
              </div>

              {/* Title with emoji */}
              <h3 className="font-heading text-xl md:text-2xl font-extrabold text-brand-950 mb-3 tracking-tight" data-id="element-36">
                <span className="mr-1.5" data-id="element-37">{bonus.emoji}</span>
                Bonus {bonus.number} — {bonus.title}
              </h3>

              {/* Description */}
              <p className="text-sm md:text-base text-gray-600 leading-relaxed" data-id="element-38">
                {bonus.desc}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
};